import React, { useState, useEffect } from "react";
import CustomInput from "../../common/CustomInput";
import { ReactSVG } from "react-svg";
import Button from "../../common/Button";
import {
  contactOptions,
  countryOptions,
  relationshipTypeThirdStepOptions,
  sameAsPersonalInfoCheckBoxOptions,
  stateOptions,
} from "./options";
import {
  isFieldError,
  isNameError,
  isValidEmail,
  isValidPhoneNumber,
  isValidPostalCode,
} from "../../../helpers/input-validations";
import ConfirmationModalNew from "../../common/ConfirmationModalNew";
import { capitalizeFirstLetter, scrollY } from "../../../helpers/functions";

const ThirdStep = ({
  currentStep,
  setCurrentStep,
  onClose,
  patientCreateObject,
  setPatientCreateObject,
}) => {
  const [lastNameCaregiver, setLastNameCaregiver] = useState(
    patientCreateObject.lastNameCaregiver
  );
  const [middleNameCaregiver, setMiddleNameCaregiver] = useState(
    patientCreateObject.middleNameCaregiver
  );
  const [firstNameCaregiver, setFirstNameCaregiver] = useState(
    patientCreateObject.firstNameCaregiver
  );
  const [relationshipTypeCaregiver, setRelationshipTypeCaregiver] = useState(
    patientCreateObject.relationshipTypeCaregiver
  );
  const [contactOptionsCaregiver, setContactOptionsCaregiver] = useState(
    patientCreateObject.contactOptionsCaregiver
  );
  const [phoneCountryCareGiver, setPhoneCountryCaregiver] = useState(
    patientCreateObject.phoneCountryCareGiver
  );
  const [secondaryPhoneCountryCareGiver, setSecondaryPhoneCountryCaregiver] =
    useState(patientCreateObject.secondaryPhoneCountryCareGiver);
  const [phoneNumberCaregiver, setPhoneNumberCaregiver] = useState(
    patientCreateObject.phoneNumberCaregiver
  );
  const [secondaryPhoneNumberCaregiver, setSecondaryPhoneNumberCaregiver] =
    useState(patientCreateObject.secondaryPhoneNumberCaregiver);
  const [emailCaregiver, setEmailCaregiver] = useState(
    patientCreateObject.emailCaregiver
  );
  const [sameAsPersonalInfoCheckBox, setSameAsPersonalInfoCheckBox] = useState(
    patientCreateObject.sameAsPersonalInfoCheckBox
  );
  const [addressCaregiver, setAddressCaregiver] = useState("");
  const [unitCaregiver, setUnitCaregiver] = useState(
    patientCreateObject.unitCaregiver
  );
  const [poBoxCaregiver, setPoBoxCaregiver] = useState(
    patientCreateObject.poBoxCaregiver
  );
  const [cityCaregiver, setCityCaregiver] = useState(
    patientCreateObject.cityCaregiver
  );
  const [stateCaregiver, setStateCaregiver] = useState(
    patientCreateObject.stateCaregiver
  );
  const [postalCodeCaregiver, setPostalCodeCaregiver] = useState(
    patientCreateObject.postalCodeCaregiver
  );
  const [countryOptionsCaregiver, setCountryOptionsCaregiver] = useState(
    patientCreateObject.countryOptionsCaregiver
  );
  const [commentsCaregiver, setCommentsCaregiver] = useState(
    patientCreateObject.commentsCaregiver
  );
  const [lastNameCaregiverError, setLastNameCaregiverError] = useState(false);
  const [middleNameCaregiverError, setMiddleNameCaregiverError] =
    useState(false);
  const [firstNameCaregiverError, setFirstNameCaregiverError] = useState(false);
  const [relationshipTypeCaregiverError, setRelationshipTypeCaregiverError] =
    useState(false);
  const [phoneNumberCaregiverError, setPhoneNumberCaregiverError] =
    useState(false);

  const [clicked, setClicked] = useState(false);
  const [openPrimaryContact, setOpenPrimaryContact] = useState(false);
  const [isSkipSelected, setIsSkipSelected] = useState(false);

  const getRequiredBg = (val) => {
    const isEmpty =
      val == null ||
      (typeof val === "string" && val.trim() === "") ||
      (Array.isArray(val) && val.length === 0);

    return isEmpty ? "!bg-blue-100" : "!bg-white";
  };

  const fieldsToCheck = [
    {
      field: lastNameCaregiver,
      checkFn: isNameError,
      setErrorFn: setLastNameCaregiverError,
      alter: false,
    },
    {
      field: middleNameCaregiver,
      checkFn: isNameError,
      setErrorFn: setMiddleNameCaregiverError,
      alter: false,
    },
    {
      field: firstNameCaregiver,
      checkFn: isNameError,
      setErrorFn: setFirstNameCaregiverError,
      alter: false,
    },
    {
      field: relationshipTypeCaregiver,
      checkFn: isFieldError,
      setErrorFn: setRelationshipTypeCaregiverError,
      alter: false,
    },
    {
      field: phoneNumberCaregiver,
      checkFn: isValidPhoneNumber,
      setErrorFn: setPhoneNumberCaregiverError,
      alter: true,
    },
  ];

  const hasFieldValidationError = ({ field, checkFn, alter }) =>
    alter ? !checkFn(field) : checkFn(field);

  const setAllValidationErrors = (forceClear = false) =>
    fieldsToCheck.forEach(({ setErrorFn, ...fieldConfig }) => {
      setErrorFn(forceClear ? false : hasFieldValidationError(fieldConfig));
    });

  const hasAnyFieldValidationError = () =>
    fieldsToCheck.some((fieldConfig) => hasFieldValidationError(fieldConfig));

  const isSaveAndContinueDisabled =
    !isSkipSelected && hasAnyFieldValidationError();

  useEffect(() => {
    if (isSkipSelected) {
      setAllValidationErrors(true);
      return;
    }

    if (clicked) {
      setAllValidationErrors();
    }
  }, [isSkipSelected, clicked, ...fieldsToCheck.map(({ field }) => field)]);

  const manageStep = () => {
    if (isSkipSelected) {
      setPatientCreateObject({
        ...patientCreateObject,
        skipCaregiver: true,
      });
      setCurrentStep(currentStep + 1);
      return;
    }

    setClicked(true);
    const hasValidationErrors = hasAnyFieldValidationError();
    setAllValidationErrors();

    if (!hasValidationErrors) {
      setPatientCreateObject({
        ...patientCreateObject,
        skipCaregiver: false,
        lastNameCaregiver,
        middleNameCaregiver,
        firstNameCaregiver,
        contactOptionsCaregiver,
        phoneCountryCareGiver,
        secondaryPhoneCountryCareGiver,
        secondaryPhoneNumberCaregiver,
        relationshipTypeCaregiver,
        phoneNumberCaregiver,
        emailCaregiver,
        sameAsPersonalInfoCheckBox,
        addressCaregiver,
        unitCaregiver,
        poBoxCaregiver,
        cityCaregiver,
        stateCaregiver,
        postalCodeCaregiver,
        countryOptionsCaregiver,
        commentsCaregiver,
      });
      setCurrentStep(currentStep + 1);
    }
  };

  const handleInputChange = (field, value) => {
    // Clear error message when input changes
    // Update corresponding state value
    switch (field) {
      case "lastNameCaregiver":
        setLastNameCaregiver(value);
        break;
      case "middleNameCaregiver":
        setMiddleNameCaregiver(value);
        break;
      case "firstNameCaregiver":
        setFirstNameCaregiver(value);
        break;
      case "relationshipTypeCaregiver":
        setRelationshipTypeCaregiver(value);
        break;
      case "phoneNumberCaregiver":
        setPhoneNumberCaregiver(value);
        break;
      // Handle other fields
      default:
        break;
    }
  };

  useEffect(() => {
    if (sameAsPersonalInfoCheckBox) {
      setAddressCaregiver(patientCreateObject.address);
      setUnitCaregiver(patientCreateObject.unit);
      setPoBoxCaregiver(patientCreateObject.poBox);
      setCityCaregiver(patientCreateObject.city);
      setStateCaregiver(patientCreateObject.state);
      setPostalCodeCaregiver(patientCreateObject.postalCode);
      setCountryOptionsCaregiver(patientCreateObject.country);
    }
  }, [sameAsPersonalInfoCheckBox]);

  useEffect(() => {
    if (currentStep === 3) {
      setIsSkipSelected(false);
    }
  }, [currentStep]);

  return (
    <>
      <div className="flex flex-col px-8 justify-between flex-1 py-6 h-full overflow-y-auto" id="form-body">
        <div className="grid grid-cols-12 gap-3">
          <div
            className="flex items-center gap-4 col-span-12 border p-4 rounded-[4px] bg-blue-100 border-blue-200"
          >
            <ReactSVG src="/icons/caution-blue.svg" className="w-10 h-10 " />
            <p className="flex items-center justify-between w-full gap-4 text-gray-700 text-body-sm">
              <span>
                <>
                  <span className="font-semibold">Skip Step:</span>{" "}
                  if there is No Alternate Caregiver.
                </>
              </span>
              <Button
                onClick={() => {
                  setPatientCreateObject({
                    ...patientCreateObject,
                    skipCaregiver: true,
                  });
                  setCurrentStep(currentStep + 1);
                }}
                className="text-body-md font-semibold py-3.5 px-8"
                text="Skip Step"
              />
            </p>
          </div>
          <CustomInput
            name="lastNameCaregiver"
            className={`col-span-4 ${getRequiredBg(lastNameCaregiver)}`}
            label="Last Name"
            value={lastNameCaregiver}
            error={lastNameCaregiverError}
            capitalizeFirstLetter={true}
            disabled={isSkipSelected}
            onChange={(e) =>
              handleInputChange("lastNameCaregiver", e.target.value)
            }
            placeholder="Enter last name"
            type="text"
            required
          />
          <CustomInput
            name="middleNameCaregiver"
            className="col-span-4"
            label="Middle Name"
            value={middleNameCaregiver}
            error={middleNameCaregiverError}
            capitalizeFirstLetter={true}
            onChange={(e) =>
              handleInputChange("middleNameCaregiver", e.target.value)
            }
            placeholder="Enter middle name"
            type="text"
          />
          <CustomInput
            name="firstNameCaregiver"
            className={`col-span-4 ${getRequiredBg(firstNameCaregiver)}`}
            label="First Name"
            value={firstNameCaregiver}
            error={firstNameCaregiverError}
            capitalizeFirstLetter={true}
            disabled={isSkipSelected}
            onChange={(e) =>
              handleInputChange("firstNameCaregiver", e.target.value)
            }
            placeholder="Enter first name"
            type="text"
            required
          />
          <CustomInput
            options={relationshipTypeThirdStepOptions}
            name="RelationshipTypeCaregiver"
            className={`col-span-12 ${getRequiredBg(relationshipTypeCaregiver)}`}
            label="Relationship"
            type="dropdown"
            onChange={(e) =>
              handleInputChange("relationshipTypeCaregiver", e.value)
            }
            disabled={isSkipSelected}
            value={relationshipTypeCaregiver}
            error={relationshipTypeCaregiverError}
            required
          />
          <CustomInput
            options={contactOptions}
            type="radio"
            name="contactOptionsCaregiver"
            className="col-span-12"
            onChange={(e) => {
              setContactOptionsCaregiver(e.target.value);
              if (
                e.target.value === contactOptions[0].value &&
                firstNameCaregiver?.length > 0 &&
                lastNameCaregiver?.length > 0
              )
                setOpenPrimaryContact(true);
            }}
            value={contactOptionsCaregiver}
            extraRadioSpace={true}
          />
          <div className="col-span-12 my-3 bg-gray-100 h-[1px] w-full " />
          <p className="col-span-12 font-medium text-gray-900 text-body-md">
            Contact Information
          </p>
          <CustomInput
            name="phoneNumberCaregiver"
            className={`col-span-6 ${getRequiredBg(phoneNumberCaregiver)}`}
            label="Primary Phone Number"
            value={phoneNumberCaregiver}
            selectValue={phoneCountryCareGiver}
            handleSelect={(e) => setPhoneCountryCaregiver(e.target.value)}
            error={phoneNumberCaregiverError}
            disabled={isSkipSelected}
            onChange={(e) =>
              handleInputChange("phoneNumberCaregiver", e.target.value)
            }
            // onFocus={(e) => scrollY("form-body")}
            placeholder="(555) 000-0000"
            type="phone"
            required
          />
          <CustomInput
            name="secondaryPhoneNumberCaregiver"
            className="col-span-6"
            label="Secondary Phone Number"
            placeholder="(555) 000-0000"
            type="phone"
            value={secondaryPhoneNumberCaregiver}
            selectValue={secondaryPhoneCountryCareGiver}
            handleSelect={(e) =>
              setSecondaryPhoneCountryCaregiver(e.target.value)
            }
            onChange={(e) => setSecondaryPhoneNumberCaregiver(e.target.value)}
            error={
              secondaryPhoneNumberCaregiver &&
              !isValidPhoneNumber(secondaryPhoneNumberCaregiver)
            }
          />
          <CustomInput
            name="emailCaregiver"
            className="col-span-12"
            label="Email Address"
            value={emailCaregiver}
            error={emailCaregiver && !isValidEmail(emailCaregiver)}
            onChange={(e) => setEmailCaregiver(e.target.value)}
            placeholder="Enter Email Address"
            type="email"
          />
          <CustomInput
            className="col-span-12 !font-semibold"
            options={sameAsPersonalInfoCheckBoxOptions}
            type="checkbox"
            name="sameAsPersonalInfoCheckBox"
            onChange={(e) => {
              // setSameAsPersonalInfoCheckBox(e.target.checked);
              setAddressCaregiver(
                e.target.checked ? patientCreateObject.address : ""
              );
              setSameAsPersonalInfoCheckBox(e.target.checked);
            }}
            value={sameAsPersonalInfoCheckBox}
          />
          <CustomInput
            name="addressCaregiver"
            className="col-span-12"
            label="Address"
            placeholder=" Enter Street Number & Street Name"
            type="text"
            onChange={(e) => setAddressCaregiver(e.target.value)}
            value={addressCaregiver}
            error={addressCaregiver && isFieldError(addressCaregiver)}
          />
          <CustomInput
            name="unitCaregiver"
            className="col-span-6"
            label="Unit Number"
            onChange={(e) => setUnitCaregiver(e.target.value)}
            value={unitCaregiver}
            error={unitCaregiver && isFieldError(unitCaregiver)}
            placeholder="Enter Unit Number"
            type="text"
          />
          <CustomInput
            name="poBoxCaregiver"
            className="col-span-6"
            label="PO Box"
            onChange={(e) => setPoBoxCaregiver(e.target.value)}
            value={poBoxCaregiver}
            error={poBoxCaregiver && isFieldError(poBoxCaregiver)}
            placeholder="PO Box"
            type="age"
          />
          <CustomInput
            name="cityCaregiver"
            className="col-span-6"
            label="City"
            onChange={(e) => setCityCaregiver(e.target.value)}
            value={cityCaregiver}
            error={cityCaregiver && isFieldError(cityCaregiver)}
            type="text"
            placeholder={"Enter City"}
          />
          <CustomInput
            options={stateOptions}
            name="stateCaregiver"
            className="col-span-6"
            label="Province/State"
            type="dropdown"
            onChange={(e) => setStateCaregiver(e.value)}
            value={stateCaregiver}
            error={stateCaregiver && isFieldError(stateCaregiver)}
          />
          <CustomInput
            name="postalCodeCaregiver"
            className="col-span-6"
            label="Postal Code"
            placeholder="Enter Postal Code"
            type="postalCode"
            onChange={(e) => setPostalCodeCaregiver(e.target.value)}
            value={postalCodeCaregiver}
            error={
              postalCodeCaregiver && !isValidPostalCode(postalCodeCaregiver)
            }
          />
          <CustomInput
            options={countryOptions}
            name="countryOptionsCaregiver"
            className="col-span-6"
            label="Country"
            type="dropdown"
            onChange={(e) => setCountryOptionsCaregiver(e.value)}
            value={countryOptionsCaregiver}
            error={
              countryOptionsCaregiver && isFieldError(countryOptionsCaregiver)
            }
          />
          <div className="col-span-12 my-1" />
          <CustomInput
            name="commentsCaregiver"
            className="col-span-12"
            label="Comments"
            onChange={(e) => setCommentsCaregiver(e.target.value)}
            value={commentsCaregiver}
            error={commentsCaregiver && isFieldError(commentsCaregiver)}
            type="text"
            placeholder={"Enter Note"}
          />
        </div>
      </div>
      <div className="flex items-center justify-center py-6">
        <div className="flex items-center justify-center space-x-5">
          <Button
            className="min-w-[200px]"
            text={currentStep === 1 ? `Cancel` : "Go Back"}
            type="secondary"
            onClick={() =>
              currentStep === 1
                ? onClose(false)
                : setCurrentStep(currentStep - 1)
            }
          />
          <Button
            className="min-w-[200px]"
            text="Save & Continue"
            type="primary"
            disabled={isSaveAndContinueDisabled}
            onClick={() => manageStep()}
          />
        </div>
      </div>
      <ConfirmationModalNew
        open={openPrimaryContact}
        title={"Primary Contact"}
        type={"primary"}
        confirmText={"Yes, Continue"}
        content={() => (
          <>
            Are you sure you want to save{" "}
            <b>
              {capitalizeFirstLetter(lastNameCaregiver)},{" "}
              {capitalizeFirstLetter(firstNameCaregiver)}
            </b>{" "}
            as your primary contact? This will mean that you will no longer be
            the primary contact.
          </>
        )}
        handleClose={() => setOpenPrimaryContact(false)}
        confirmAction={() => setOpenPrimaryContact(false)}
      />
    </>
  );
};

export default ThirdStep;
